import { useCallback, useRef } from 'react';
import { getAllVendorsExpanded } from '../lib/vendorApi';
import { sortVendorsByDateAndTime } from '../lib/vendorUtils';
import { generateCacheKey, hasBoundsChangedSignificantly, dateRangesEqual } from '../lib/mapUtils';
import { DateRange, MapBounds, CacheEntry, Vendor } from '../types/types';
import { APP_CONFIG, DEFAULT_CATEGORIES } from '../lib/appConstants';

interface UseFetchVendorsProps {
  locationState: 'pending' | 'granted' | 'denied' | 'unavailable';
  mapBounds: MapBounds | null;
  apiDateRange: DateRange;
  apiCache: Map<string, CacheEntry>;
  setApiCache: React.Dispatch<React.SetStateAction<Map<string, CacheEntry>>>;
  onVendors: (vendors: Vendor[]) => void;
  setCategories: React.Dispatch<React.SetStateAction<string[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsInitialLoad: React.Dispatch<React.SetStateAction<boolean>>;
  isInitialLoad: boolean;
  setCuratedLists: React.Dispatch<React.SetStateAction<any[]>>;
  onFetchSuccess?: (serverTime: string) => void;
}

export const useFetchVendors = ({
  locationState,
  mapBounds,
  apiDateRange,
  apiCache,
  setApiCache,
  onVendors,
  setCategories,
  setIsLoading,
  setIsInitialLoad,
  isInitialLoad,
  setCuratedLists,
  onFetchSuccess,
}: UseFetchVendorsProps) => {
  const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastBoundsRef = useRef<MapBounds | null>(null);
  const lastDateRangeRef = useRef<DateRange | null>(null);

  const fetchVendors = useCallback(
    async (options: { force?: boolean; immediate?: boolean; bustCache?: boolean } = {}) => {
      // Clear any pending request
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }

      if (locationState === 'pending') {
        return;
      }

      // Check if bounds have changed significantly (UNLESS force=true for explicit user refresh)
      if (!options.force && mapBounds && lastBoundsRef.current) {
        if (
          !hasBoundsChangedSignificantly(lastBoundsRef.current, mapBounds) &&
          dateRangesEqual(apiDateRange, lastDateRangeRef.current)
        ) {
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
          setIsLoading(false);
          return;
        }
      }

      // Check cache first
      const cacheKey = generateCacheKey(mapBounds, apiDateRange);
      const cachedEntry = apiCache.get(cacheKey);

      if (
        !options.bustCache &&
        cachedEntry &&
        Date.now() - cachedEntry.timestamp < APP_CONFIG.CACHE_DURATION
      ) {
        onVendors(cachedEntry.data);
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
        setIsLoading(false);
        lastBoundsRef.current = mapBounds;
        lastDateRangeRef.current = apiDateRange;
        return;
      }

      // Set a debounced delay for API requests
      requestTimeoutRef.current = setTimeout(
        async () => {
          try {
            setIsLoading(true);

            const filters = {
              startDate: apiDateRange.startDate,
              endDate: apiDateRange.endDate,
              ...(mapBounds ? { mapBounds } : {}),
            };

            // Perform API request - use EXPANDED endpoint
            const { vendors: vendorsData, curatedLists } = await getAllVendorsExpanded(filters);
            const sortedVendors = sortVendorsByDateAndTime(vendorsData);

            // Create cache entry
            const newCacheEntry: CacheEntry = {
              data: sortedVendors,
              bounds: mapBounds!,
              timestamp: Date.now(),
              dateRange: apiDateRange,
            };

            // Update cache with new entry
            setApiCache(prevCache => {
              const newCache = new Map(prevCache);

              // Clean old entries first
              const now = Date.now();
              const keysToDelete: string[] = [];

              for (const [key, entry] of newCache.entries()) {
                if (now - entry.timestamp >= APP_CONFIG.CACHE_DURATION) {
                  keysToDelete.push(key);
                }
              }

              keysToDelete.forEach(key => newCache.delete(key));

              // Add new entry
              newCache.set(cacheKey, newCacheEntry);

              // Limit cache size
              if (newCache.size > APP_CONFIG.MAX_CACHE_ENTRIES) {
                const sortedEntries = Array.from(newCache.entries())
                  .sort((a, b) => b[1].timestamp - a[1].timestamp)
                  .slice(0, APP_CONFIG.MAX_CACHE_ENTRIES);

                newCache.clear();
                sortedEntries.forEach(([key, entry]) => newCache.set(key, entry));
              }

              return newCache;
            });

            onVendors(sortedVendors);
            setCuratedLists(curatedLists);
            onFetchSuccess?.(new Date().toISOString());

            // Update categories based on returned data, ensuring defaults are always present
            const apiCategories = Array.from(new Set(sortedVendors.map(vendor => vendor.category)));
            // Merge API categories with defaults, keeping defaults first
            const mergedCategories = Array.from(new Set([...DEFAULT_CATEGORIES, ...apiCategories]));
            setCategories(mergedCategories);
            if (isInitialLoad) {
              setIsInitialLoad(false);
            }
            setIsLoading(false);
            lastBoundsRef.current = mapBounds;
            lastDateRangeRef.current = apiDateRange;
          } catch (_error) {
            if (isInitialLoad) {
              setIsInitialLoad(false);
            }
            setIsLoading(false);
            setCuratedLists([]);
          }
        },
        options.immediate ? 0 : APP_CONFIG.API_DEBOUNCE_TIME
      );
    },
    [
      locationState,
      mapBounds,
      apiDateRange,
      apiCache,
      setApiCache,
      onVendors,
      setCategories,
      setIsLoading,
      setIsInitialLoad,
      isInitialLoad,
      setCuratedLists,
      onFetchSuccess,
    ]
  );

  return { fetchVendors };
};
